// Native scroll snapping retains touch navigation; buttons enhance the same track.
class PortraitChooserCarousel extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('.portrait-chooser__images');
    this.previous = this.querySelector('.portrait-chooser__arrow--previous');
    this.next = this.querySelector('.portrait-chooser__arrow--next');
    if (!this.track || !this.previous || !this.next) return;
    this.previous.addEventListener('click', () => this.move(this.index() - 1), { signal });
    this.next.addEventListener('click', () => this.move(this.index() + 1), { signal });
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 180);
    }, { signal, passive: true });
    this.track.addEventListener('scrollend', () => this.settle(), { signal });
    this.track.addEventListener('touchstart', () => { this.touching = true; }, { signal, passive: true });
    const touchEnd = () => { this.touching = false; clearTimeout(this.settleTimer); this.settleTimer = setTimeout(() => this.settle(), 180); };
    this.track.addEventListener('touchend', touchEnd, { signal, passive: true });
    this.track.addEventListener('touchcancel', touchEnd, { signal, passive: true });
    this.track.addEventListener('keydown', event => {
      const destinations = { ArrowLeft: this.index() - 1, ArrowRight: this.index() + 1, Home: 0, End: this.slides().length - 1 };
      if (!(event.key in destinations)) return;
      event.preventDefault();
      this.move(destinations[event.key]);
    }, { signal });
    this.removeClones();
    for (const image of this.slides()) {
      image.addEventListener('error', () => { image.hidden = true; this.rebuild(); }, { signal });
      if (image.complete && !image.naturalWidth) image.hidden = true;
    }
    this.observer?.disconnect();
    this.rebuild();
    this.observer = new ResizeObserver(() => { this.jump(this.slides()[this.active || 0]); this.sync(); });
    this.observer.observe(this.track);
    this.sync();
  }
  slides() { return [...this.track.querySelectorAll('img:not([hidden]):not([data-carousel-clone])')]; }
  removeClones() { this.track.querySelectorAll('[data-carousel-clone]').forEach(image => image.remove()); }
  rebuild() {
    this.removeClones();
    const slides = this.slides();
    if (slides.length > 1) {
      const clone = image => {
        const copy = image.cloneNode(true);
        copy.dataset.carouselClone = 'true';
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('alt', '');
        copy.removeAttribute('id');
        copy.loading = 'eager';
        copy.inert = true;
        return copy;
      };
      this.track.prepend(clone(slides[slides.length - 1]));
      this.track.append(clone(slides[0]));
    }
    this.active = 0;
    this.jump(slides[0]);
    this.sync();
  }
  nearest() {
    const left = this.track.getBoundingClientRect().left;
    return [...this.track.querySelectorAll('img:not([hidden])')].sort((a,b) =>
      Math.abs(a.getBoundingClientRect().left-left)-Math.abs(b.getBoundingClientRect().left-left))[0];
  }
  index() {
    const slides = this.slides(), image = this.nearest();
    if (image?.dataset.carouselClone) return image === this.track.firstElementChild ? slides.length - 1 : 0;
    return Math.max(0, slides.indexOf(image));
  }
  jump(image) {
    if (!image) return;
    this.track.scrollBy({ left: image.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: 'instant' });
  }
  settle() {
    clearTimeout(this.settleTimer);
    if (this.touching) return;
    const image = this.nearest();
    if (!image || Math.abs(image.getBoundingClientRect().left-this.track.getBoundingClientRect().left)>1) return;
    this.active = this.index();
    if (image.dataset.carouselClone) this.jump(this.slides()[this.active]);
  }
  move(index) {
    const slides = this.slides();
    if (!slides.length) return;
    // Normalize a settled boundary before starting the next directional movement.
    this.settle();
    const image = index < 0 ? this.track.firstElementChild : index >= slides.length ? this.track.lastElementChild : slides[index];
    if (!image) return;
    this.track.scrollBy({ left: image.getBoundingClientRect().left - this.track.getBoundingClientRect().left,
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  sync() {
    const count = this.slides().length;
    this.previous.hidden = this.next.hidden = count < 2;
    this.previous.disabled = this.next.disabled = count < 2;
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); clearTimeout(this.settleTimer); this.removeClones(); }
}
if (!customElements.get('la-portrait-carousel')) customElements.define('la-portrait-carousel', PortraitChooserCarousel);


class PortraitCardGallery extends HTMLElement {
 connectedCallback() {
  this.abort?.abort(); this.abort = new AbortController();
  const signal = this.abort.signal;
  this.slides = [...this.querySelectorAll('[data-set-slide]')];
  this.index = 0;
  this.querySelectorAll('[data-card-direction]').forEach(button => {
   button.hidden = this.slides.length < 2;
   button.disabled = this.slides.length < 2;
  });
  this.show = n => {
   if (!this.slides.length) return;
   this.index = (n + this.slides.length) % this.slides.length;
   this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
   this.querySelector('[data-card-status]').textContent = 'Image ' + (this.index + 1) + ' of ' + this.slides.length;
  };
  this.addEventListener('click', event => {
   if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; return; }
   const button = event.target.closest('[data-card-direction]');
   if (button) { event.preventDefault(); event.stopPropagation(); this.show(this.index + Number(button.dataset.cardDirection)); }
  }, {signal});
  this.addEventListener('keydown', event => {
   if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    event.preventDefault(); event.stopPropagation(); this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
   }
  }, {signal});
  this.addEventListener('pointerdown', event => {
   this.start = event.isPrimary && !event.target.closest('button') ? {x:event.clientX, y:event.clientY, id:event.pointerId} : null;
   this.suppressClick = false;
  }, {signal});
  this.addEventListener('pointerup', event => {
   if (!this.start || event.pointerId !== this.start.id) return;
   const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
   this.start = null;
   if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.suppressClick = true;
   if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1));
  }, {signal});
  this.addEventListener('pointercancel', () => {this.start = null;}, {signal});
  this.addEventListener('dragstart', event => {event.preventDefault();}, {signal});
  this.show(0);
 }
 disconnectedCallback() {this.abort?.abort();}
}
if (!customElements.get('portrait-card-gallery')) customElements.define('portrait-card-gallery', PortraitCardGallery);
